import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import ExpandableImage from "../ExpandableImage";
import Linkify from "react-linkify";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import UserAvatar from "../UserAvatar";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IMG_PLACEHOLDER, STORAGE_BUCKET } from "../constants";

const PLACEHOLDER_IMAGE = `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`;

function getItemThumb(item) {
  return (item.images || []).find(Boolean) || PLACEHOLDER_IMAGE;
}

function ChatImage(props) {
  const { pic, items, onAddImageToItem, saving, addImageLabel } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [menu, setMenu] = useState(null); // "actions" | "items" | null
  const wasSaving = useRef(false);
  const actionLabel = addImageLabel || "Add to item";

  const closeMenus = () => {
    setAnchorEl(null);
    setMenu(null);
  };

  useEffect(() => {
    if (wasSaving.current && !saving) {
      closeMenus();
    }
    wasSaving.current = saving;
  }, [saving]);

  const handleAddToItem = () => {
    if (!items?.length) {
      onAddImageToItem?.(pic, null);
      return;
    }
    if (items.length === 1) {
      onAddImageToItem(pic, 0);
      return;
    }
    setMenu("items");
  };

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      <ExpandableImage src={pic} style={{ height: "200px", borderRadius: "5px" }} />
      {onAddImageToItem && (
        <>
          <IconButton
            size="small"
            disabled={saving}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
              setMenu("actions");
            }}
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              backgroundColor: "rgba(0,0,0,0.45)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menu === "actions"}
            onClose={() => !saving && closeMenus()}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem onClick={handleAddToItem} disabled={saving}>
              {saving && items?.length === 1 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  Adding…
                </Box>
              ) : (
                actionLabel
              )}
            </MenuItem>
          </Menu>
          <Menu
            anchorEl={anchorEl}
            open={menu === "items"}
            onClose={() => !saving && closeMenus()}
            onClick={(e) => e.stopPropagation()}
            MenuListProps={{
              subheader: (
                <ListSubheader component="div" sx={{ lineHeight: "36px" }}>
                  Add to which item?
                </ListSubheader>
              ),
            }}
            slotProps={{
              paper: { sx: { maxWidth: 320 } },
            }}
          >
            {items?.map((item, idx) => (
              <MenuItem
                key={idx}
                disabled={saving}
                onClick={() => onAddImageToItem(pic, idx)}
              >
                <ListItemIcon sx={{ minWidth: 44 }}>
                  <img
                    alt=""
                    src={getItemThumb(item)}
                    style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4 }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={`Qty ${item.quantity}`}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </MenuItem>
            ))}
            {saving && (
              <Box display="flex" justifyContent="center" py={1}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
}

export default function ChatBubble(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  let style = {
    padding: "7px 10px 5px 10px",
    marginBottom: "1rem",
    maxWidth: isMobile ? "320px" : "480px",
    display: "inline-block",
    wordWrap: "break-word",
  };

  let justify = "flex-start";

  if (props.chat.by === "cust") {
    style.backgroundColor = "#ececec";
  } else {
    style.backgroundColor = "#ceb4c1";
    justify = "flex-end";
  }

  return (
    <Box style={{ display: "flex", justifyContent: justify }}>
      <UserAvatar uid={props.chat.uid} by={props.chat.by} />
      <Paper elevation={0} style={style}>
        {props.chat.pics &&
          props.chat.pics.map((pic, idx) => {
            return (
              <Box key={idx}>
                <ChatImage
                  pic={pic}
                  items={props.items}
                  onAddImageToItem={props.onAddImageToItem}
                  saving={props.saving}
                  addImageLabel={props.addImageLabel}
                />
              </Box>
            );
          })}
        <Linkify>
          <Typography variant="body2" display="inline">
            {props.chat.msg}
          </Typography>
        </Linkify>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ float: "right", marginLeft: "2rem" }}
        >
          {moment(props.chat.ts).format("h:mm A")}
        </Typography>
      </Paper>
    </Box>
  );
}
